import { fmtTime } from '../lib/dates.ts';
import { useEffect, useRef, useState } from 'react';
import { chat, files, type ChatMessage } from '../lib/api.ts';
import { onLive } from '../lib/live.ts';

interface Props {
  readonly sessionId: string;
  readonly initial: ChatMessage[];
  readonly staffPresent: string[];
  /** Devices can sign a message; accounts are already named. */
  readonly canSign?: boolean;
  readonly onClose?: () => void;
  /** Staff only: turn this conversation into a tracked request. */
  readonly onEscalate?: () => void;
  /** History view: a transcript, not a live room. */
  readonly readOnly?: boolean;
}

/**
 * One conversation.
 *
 * Polls rather than relying only on the socket: a chat that silently
 * stops updating is worse than one that updates a second late, and the
 * shell has to work when the WebSocket has dropped on retail wifi.
 */
export const ChatRoom = ({
  sessionId,
  initial,
  staffPresent,
  canSign,
  onClose,
  onEscalate,
  readOnly,
}: Props) => {
  const [messages, setMessages] = useState<ChatMessage[]>(initial);
  const [present, setPresent] = useState(staffPresent);
  const [body, setBody] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const foot = useRef<HTMLDivElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});

  // Only ever asks for what it has not seen.
  const highest = () => messages.at(-1)?.seq;

  /**
   * Append, ignoring anything already on screen.
   *
   * Four callers add messages: the mount fetch, the poll, the live
   * event and your own send. Two arriving for the same message is
   * normal, so the list refuses duplicates rather than each caller
   * guessing whether it is first.
   */
  const merge = (incoming: readonly ChatMessage[]) => {
    if (incoming.length === 0) return;
    setMessages((m) => {
      const seen = new Set(m.map((x) => x.id));
      const fresh = incoming.filter((x) => !seen.has(x.id));
      return fresh.length ? [...m, ...fresh] : m;
    });
  };

  // Fetched once on arrival, live room or transcript alike. A live
  // room used to wait for the first poll, so switching sites showed
  // an empty conversation for up to four seconds before the history
  // appeared.
  useEffect(() => {
    if (messages.length > 0) return;
    chat
      .poll(sessionId)
      .then((r) => setMessages(r.messages))
      .catch(() => undefined);
  }, [sessionId]);

  useEffect(() => {
    // Nothing to poll for in a closed transcript.
    if (readOnly) return;

    const id = window.setInterval(() => {
      chat
        .poll(sessionId, highest())
        .then((r) => {
          if (r.messages.length > 0) {
            merge(r.messages);
          }
          setPresent(r.staffPresent);
        })
        .catch(() => undefined);
    }, 4000);

    // The socket is the mechanism; the four second poll is the
    // safety net for a dropped connection. Someone waiting on an
    // answer should not watch a blank screen for four seconds.
    const off = onLive((e) => {
      if (e.type !== 'chat') return;
      if (e.sessionId && e.sessionId !== sessionId) return;
      chat
        .poll(sessionId, highest())
        .then((r) => {
          if (r.messages.length > 0) {
            merge(r.messages);
          }
          setPresent(r.staffPresent);
        })
        .catch(() => undefined);
    });

    return () => {
      off();
      window.clearInterval(id);
    };
  }, [sessionId, messages]);


  // Signed thumb URLs are fetched per message and not held: they last
  // 60 seconds, so a batch prefetched at load would be dead by the
  // time anyone scrolled to them.
  useEffect(() => {
    for (const m of messages) {
      for (const id of m.files) {
        if (thumbs[id]) continue;
        files
          .sign(id, 'thumb')
          .then((r) =>
            setThumbs((t) => ({ ...t, [id]: r.url })),
          )
          .catch(() => undefined);
      }
    }
  }, [messages]);

  // Scroll on arrival, not on every render: a counter screen that
  // yanks itself to the bottom while someone is reading back is worse
  // than one that stays put.
  useEffect(() => {
    // 'nearest' rather than the default: without an inner scroll
    // container this would drag the whole page on every message,
    // which on a counter screen yanks the board out from under
    // whoever is reading it.
    foot.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      await chat.send(sessionId, text, pin.length >= 4 ? pin : undefined);
      setBody('');
      const r = await chat.poll(sessionId, highest());
      merge(r.messages);
    } catch {
      // Left in the box so nothing is lost to a dropped connection.
    } finally {
      setBusy(false);
    }
  };


  // A photo needs a message to hang from, so an empty box becomes a
  // one-word message rather than failing. "Here is a photo of the
  // broken thing" is the commonest thing a site sends.
  const attachFile = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    try {
      const sent = await chat.send(
        sessionId,
        body.trim() || 'Photo',
        pin.length >= 4 ? pin : undefined,
      );
      await chat.attach(sent.id, file);
      setBody('');
      const r = await chat.poll(sessionId, highest());
      merge(r.messages);
    } catch {
      // Nothing lost: the text stays in the box.
    } finally {
      setBusy(false);
    }
  };


  // Ctrl+V a screenshot. For IT this is the most useful thing a
  // person can send -- describing an error dialog in words is how
  // half an hour goes on the wrong problem.
  const onPaste = (e: React.ClipboardEvent) => {
    const item = [...e.clipboardData.items].find((i) =>
      i.type.startsWith('image/'),
    );
    if (!item) return;
    const file = item.getAsFile();
    if (file) {
      e.preventDefault();
      void attachFile(file);
    }
  };

  const when = (iso: string): string =>
    fmtTime(iso);

  return (
    <div className="chatRoom">
      <p className="chatPresence mono">
        {present.length > 0
          ? `${present.length} available: ${present.join(', ')}`
          : 'Nobody is watching this yet'}
      </p>

      <div className="chatLog">
        {messages.map((m) => (
          <div
            key={m.id}
            className={m.fromSite ? 'chatMsg site' : 'chatMsg hq'}
          >
            <span className="chatWho mono">
              {m.author} &middot; {when(m.createdAt)}
            </span>
            <p>{m.body}</p>
            {m.files.length > 0 && (
              <div className="chatFiles">
                {m.files.map((id) =>
                  thumbs[id] ? (
                    <img key={id} src={thumbs[id]} alt="attachment" />
                  ) : null,
                )}
              </div>
            )}

          </div>
        ))}
        <div ref={foot} />
      </div>

      {!readOnly && (
      <div className="chatSend">
        <textarea
          rows={2}
          value={body}
          placeholder="Type a message, or paste a screenshot"
          onPaste={onPaste}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <div className="noticeRow">
          <input
            ref={picker}
            type="file"
            accept="image/png,image/jpeg,application/pdf"
            capture="environment"
            hidden
            onChange={(e) => void attachFile(e.target.files?.[0])}
          />
          <button
            className="iconBtn"
            title="Attach a photo"
            aria-label="Attach a photo"
            onClick={() => picker.current?.click()}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="7" width="18" height="13" rx="2" />
              <path d="M9 7 L10.5 4 h3 L15 7" />
              <circle cx="12" cy="13.5" r="3.2" />
            </svg>
          </button>
          {canSign && (
            <input
              className="pinInput"
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              placeholder="PIN"
              title="Optional: puts your name on your messages"
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            />
          )}
          <button
            className="primary"
            disabled={busy || !body.trim()}
            onClick={() => void send()}
          >
            Send
          </button>
          {onEscalate && (
            <button onClick={onEscalate}>Make this a ticket</button>
          )}
          {onClose && (
            <button onClick={onClose}>Close conversation</button>
          )}
        </div>
      </div>
      )}
    </div>
  );
};
